/**
 * Original source https://github.com/srid/imako/tree/master
 * Commit 5ac0382b0795f0897eea866fe335586a3083bc9c
 */

import { LitElement, html, css, TemplateResult, nothing } from "lit";
import { property } from "lit/decorators.js";
import type { OutDesignNode } from "./types";

import "./BlockRenderer";
import "../ui/OutDesignInlineElement";
import { imagesObjectURLMap } from "../ui/shared";
import { sanitize } from "../ui/utils";
import { OutDesignNoteElement } from "../ui/OutDesignNoteElement";

OutDesignNoteElement;

// List of common supported MIME types in browsers
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/MIME_types#image_types
const supportedImageTypes: Array<string> = [
  "image/apng",
  "image/avif",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/svg+xml",
  "image/webp",
]

export class InlineRenderer extends LitElement {
  @property({ attribute: false })
  inlines: OutDesignNode[] = [];

  static styles = css`
    :host {
      display: inline;
    }

    .imageError {
      background: #F67D3113;
      outline: dotted 2px lightgray;
      padding: 0.5em;
      text-align: center;
      margin: 0.2em;
      font-family: monospace;
    }

    .image {
      text-align: center
      display: block;
      text-align: center;
    }
    
    .image p {
      font-family: monospace;
      text-wrap: balance;
      background: rgba(255, 255, 255, 0.8);
      z-index: 10;
      position: absolute;
      text-align: center;
      margin: 0px;
      height: -webkit-fill-available;
      width: -webkit-fill-available;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `;

  render() {
    return html`${this.inlines.map((i) => this.renderInline(i))}`;
  }

  renderInline(inline: OutDesignNode) {    
    switch (inline.type) {
      case "Str":
        return inline.data?.text;

      case "Space":
      case "SoftBreak":
        return " ";

      case "LineBreak":
        return html`<od-inline .node=${inline}><br /></od-inline>`;

      case "Emph":
        return html`
        <od-inline .node=${inline}>
          <em>
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </em>
        </od-inline>`;

      case "Strong":
        return html`
        <od-inline .node=${inline}>
          <strong>
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </strong>
        </od-inline>`;

      case "Underline":
        return html`
        <od-inline .node=${inline}>
          <u>
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </u>
        </od-inline>`;

      case "Strikeout":
        return html`
        <od-inline .node=${inline}>
          <del>
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </del>
        </od-inline>`;

      case "Superscript":
        return html`
        <od-inline .node=${inline}>
          <sup>
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </sup>
        </od-inline>`;

      case "Subscript":
        return html`
        <od-inline .node=${inline}>
          <sub>
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </sub>
        </od-inline>`;

      case "SmallCaps":
        return html`
        <od-inline .node=${inline}>
          <span style="font-variant: small-caps">
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </span>
        </od-inline>`;

      case "Code": {
        const code = inline.data?.text
        return html`
        <od-inline .node=${inline}>
          <code>
            ${code}
          </code>
        </od-inline>`;
      }

      case "Link": {
        const url = inline.data?.url

        // const isBroken = kvs?.some(([k]) => k === "data-broken");
        // const isWikilink = kvs?.some(([k]) => k === "data-wikilink");

        // const wikilinkTarget =
        //   kvs?.find(([k]) => k === "data-wikilink")?.[1] ?? "";

        // const isInternal = (url as string).startsWith("/p/");

        // if (isBroken) {
        //   const display =
        //     inlines.length > 0
        //       ? this.inlineText(inlines)
        //       : wikilinkTarget || url;

        //   return html`
        //     <span
        //       title="Broken link: ${wikilinkTarget || url}"
        //     >
        //       ${display}
        //     </span>
        //   `;
        // }

        // if (isInternal) {
        //   const display =
        //     inlines.length > 0
        //       ? this.inlineText(inlines)
        //       : wikilinkTarget || decodeURIComponent(url.slice(3));

        //   const click = () => {
        //     window.dispatchEvent(
        //       new CustomEvent("app:navigate", { detail: url })
        //     );
        //   };

        //   return html`
        //     <span
        //       class=${isWikilink}
        //       @click=${click}
        //     >
        //       ${display}
        //     </span>
        //   `;
        // }

        return html`
        <od-inline .node=${inline}>
          <a href=${url} target="_blank" rel="noopener">
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </a>
        </od-inline>
        `;
      }

      case "Image": {
        const [alt, src, title] = [inline.data?.alt, inline.data?.src, inline.data?.title];
        const altText = this.inlineText(alt as unknown as OutDesignNode[]);

        let errorLabel: TemplateResult = html``;

        const missingImageTemplate = html`
          <div
            id=${inline.attr?.[0]}
            class=${["imageError", ...inline.attr?.[1] as Array<string>]}
            >
            ${errorLabel}
            ${title ? html`<p><b>title:</b> ${title}</p>` : nothing }
            ${altText ? html`<p><b>alt:</b> ${altText}</p>` : html`<p><b>Missing alt-text</b></p>` }
          </div>
          `
        const loadedImage = imagesObjectURLMap[sanitize(src.split("/").pop())]

        if(!loadedImage) return missingImageTemplate;

        const imageUrl: string = loadedImage.url

        if(!imageUrl) {
          errorLabel = html`<p>The image <b>${loadedImage.filename}</b> could not be loaded.<p>`
        }

        if(!supportedImageTypes.includes(loadedImage.type)) {
          errorLabel = html`<p>The image <b>${loadedImage.filename}</b> can not be displayed.<br /><b>The type ${loadedImage.type}</b> is not supported.<p>`
        }

        if(!imageUrl || !supportedImageTypes.includes(loadedImage.type)) return missingImageTemplate;

        return html`
          <div class="image">
            ${altText ? html`<p><b>alt:</b> ${altText}</p>` : html`<p><b>Missing alt-text</b></p>` }
            <img
              id=${inline.attr?.[0]}
              class=${inline.attr?.[1]}
              src=${imageUrl ?? src}
              alt=${altText}
              title=${title || ""}
            />
          </div>
        `;
      }

      // case "Quoted": {
      //   const [type, inlines] = inline.children;

      //   const q = type.t === "SingleQuote" ? "'" : '"';

      //   return html`${q}<inline-renderer
      //       .inlines=${inlines}
      //     ></inline-renderer>${q}`;
      // }

      // case "RawInline": {
      //   const [format, text] = inline.children;

      //   if (format === "html") {
      //     return html`<span .innerHTML=${text}></span>`;
      //   }

      //   return html`<code>${text}</code>`;
      // }

      case "Note":
        // simplified: render blocks inline
        return html`
          <od-note>
            <od-block .node=${inline}>
              <block-renderer .blocks=${inline.children}></block-renderer>
            </od-block>
          </od-note>
        `;

      case "Span": {
        // if (classes.includes("wikilink")) {
        //   const target = this.inlineText(inlines);

        //   return html`
        //     <span
        //       classes=${classes} 
        //       title="Link to: ${target}"
        //     >
        //       ${target}
        //     </span>
        //   `;
        // }

          return html`
          <od-inline .node=${inline}>
            <inline-renderer .inlines=${inline.children}></inline-renderer>
          </od-inline>  
          `
        }


      // case "Cite": {
      //   const [_citations, inlines] = inline.children;

      //   return html`
      //     <inline-renderer .inlines=${inlines}></inline-renderer>
      //   `;
      // }

      default:
        return html``;
    }
  }

  inlineText(inlines: Array<OutDesignNode>) {
    return inlines
      .map((i) => {
        if (i.type === "Str") return i.data?.text;
        if (i.type === "Space") return " ";
        return "";
      })
      .join("");
  }
}

customElements.define("inline-renderer", InlineRenderer);